/**
 * Inbox cancel-intent prompt — shared types + the canonical cancel-reason list.
 *
 * When a creator says something in the inbox that reads as "I need to cancel", the inbox agent
 * posts a confirmation message carrying two quick-reply chips. This is deliberately a SEPARATE
 * option type from CheckInMessageOption: check-in chips route into
 * CollabCheckInService.recordPreVisitResponse, which only answers an OPEN pre-visit check-in and
 * would no-op (or corrupt preVisitCheckInStatus) for a prompt that has nothing to do with one.
 *
 * MIRRORED in foodfluence_dashboard/shared/src/types/cancelIntent.ts — the dashboard writes these
 * values onto the message document and the web app renders them. Keep the two copies identical.
 */

/**
 * What a cancel-intent chip does when tapped.
 *
 * 'rescheduleInstead' hands the conversation to the rescheduling agent. It is offered whenever
 * that agent could actually take it, decided by a live availability probe and NOT by a campaign
 * type or a time rule: inbox reschedules have no proximity cutoff, and what settles them is
 * whether the campaign has bookable slots left. Offered first and styled as the primary action,
 * because a moved visit keeps the collab alive where a cancel ends it.
 *
 * 'keepCollab' is the escape hatch for a misread. Intent detection runs at 0.75 confidence and
 * still misfires; without a way to say "I didn't ask for this", every button on an unprompted
 * prompt would be an action on a collab the creator was perfectly happy with.
 *
 * 'undoCancel' rides the same chip mechanism but appears only AFTER a cancel, on the agent's
 * "I've canceled this" message, and only inside CREATOR_UNDO_WINDOW_MS. It is the creator's own
 * escape hatch for a mis-tap — the staff Uncancel it delegates to is otherwise brand-side only.
 */
export type CancelIntentAction =
  | 'rescheduleInstead'
  | 'confirmCancel'
  | 'keepCollab'
  | 'undoCancel';

export interface CancelIntentOption {
  action: CancelIntentAction;
  /** Label shown on the chip and echoed into the thread as the creator's reply. */
  label: string;
}

/**
 * The canonical reasons a creator cancels, as offered by the web app's CancelOptionsModal.
 *
 * The inbox agent has no modal to show, so an LLM classifies the creator's own wording into one
 * of these instead — that keeps `influencerCancelReason` comparable across both entry points
 * rather than filling the field with free text only from the inbox.
 *
 * These exact strings are what get written to Firestore, so they are the interface, not the keys.
 */
export const COLLAB_CANCEL_REASONS = {
  scheduleConflict: 'A schedule conflict came up',
  brandFit: 'No longer want to collaborate with this brand due to brand fit',
  compensation: 'My compensation rates have changed since I accepted this collab',
} as const;

export type CollabCancelReasonKey = keyof typeof COLLAB_CANCEL_REASONS;

/** Keys the reason classifier is allowed to return (plus null when it can't tell). */
export const COLLAB_CANCEL_REASON_KEYS = Object.keys(COLLAB_CANCEL_REASONS) as CollabCancelReasonKey[];

/**
 * Written when the classifier cannot place the creator's message in any of the reasons above.
 *
 * Deliberately not one of the three: guessing "a schedule conflict came up" would put a cause the
 * creator never gave into the brand's cancellation data, where it is indistinguishable from a real
 * one. This says only what we actually know.
 *
 * Says nothing about WHERE the cancel came from — the reason is rendered inside a note that
 * already names the creator and is already suffixed "(via inbox)", so carrying the provenance here
 * too produced "canceled the collab — Canceled from the inbox — reason not specified (via inbox)".
 */
export const UNSPECIFIED_INBOX_CANCEL_REASON = 'No reason given';

/** Resolve a classifier result to the string stored on the collab. */
export const cancelReasonForKey = (key?: CollabCancelReasonKey | null): string =>
  (key && COLLAB_CANCEL_REASONS[key]) || UNSPECIFIED_INBOX_CANCEL_REASON;

/**
 * Appended to every reason the inbox agent stores, marking where the cancel came from.
 *
 * Shared rather than private to the agent because the uncancel rule keys off it: the reason is the
 * only durable trace on the collab of HOW it was canceled, and an inbox cancel has to be
 * recognisable as one to be undoable.
 */
export const INBOX_CANCEL_REASON_SUFFIX = ' (via inbox)';

/** Was this collab canceled by the inbox agent? Reads the provenance suffix off the stored reason. */
export const isInboxCancelReason = (reason?: string | null): boolean =>
  typeof reason === 'string' && reason.endsWith(INBOX_CANCEL_REASON_SUFFIX);

/**
 * How long after an inbox cancel the creator may undo it themselves.
 *
 * Deliberately minutes, not the staff uncancel window's days: this covers "I got that wrong",
 * including noticing after putting the phone down, but not a change of mind. Past it the collab is
 * genuinely canceled — the brand has been told, the funds released — and reversing it is a support
 * conversation.
 *
 * Comfortably inside the cancel cutoff, so an undo can never land on a visit that has already
 * happened: a collab cannot be canceled within INFLUENCER_CANCEL_CUTOFF_HOURS of its visit, so the
 * visit is still hours out when the window closes.
 */
export const CREATOR_UNDO_WINDOW_MS = 30 * 60 * 1000;
